import {
  BENCH_POSITIONS,
  type LeagueData,
  type OptimalSlot,
  type Player,
  type RosterSlots,
  type SlotEligibility,
  type TeamWeek,
} from "./schemas.js";

/**
 * Build the optimal lineup for a team-week: the highest-scoring set of starters
 * the manager *could* have played, given every rostered player's actual points
 * (bench included) and the league's roster slots.
 *
 * Football roster slots form a laminar (nested) eligibility structure —
 * dedicated slots accept one position, flex slots accept a superset — so a
 * greedy that fills the most restrictive slots first, always taking the
 * highest-scoring eligible player still available, is provably optimal.
 */

type ExpandedSlot = { name: string; accepted: Set<string> };

function expandSlots(
  rosterSlots: RosterSlots,
  slotEligibility: SlotEligibility,
): ExpandedSlot[] {
  const slots: ExpandedSlot[] = [];
  for (const [slotName, count] of Object.entries(rosterSlots)) {
    if (BENCH_POSITIONS.has(slotName)) continue;
    const accepted = new Set(slotEligibility[slotName] ?? [slotName]);
    for (let i = 0; i < count; i++) slots.push({ name: slotName, accepted });
  }
  // Most restrictive (fewest accepted positions) first.
  slots.sort((a, b) => a.accepted.size - b.accepted.size);
  return slots;
}

const eligibleFor = (player: Player, accepted: Set<string>): boolean =>
  player.positions.some((pos) => accepted.has(pos));

const round2 = (n: number): number => Math.round(n * 100) / 100;

export interface OptimalResult {
  points: number;
  lineup: OptimalSlot[];
}

export function optimizeLineup(
  players: Player[],
  rosterSlots: RosterSlots,
  slotEligibility: SlotEligibility,
): OptimalResult {
  const slots = expandSlots(rosterSlots, slotEligibility);
  // Highest scorers first so a single greedy pass suffices.
  const available = [...players].sort((a, b) => b.points - a.points);
  const used = new Set<Player>();

  const lineup: OptimalSlot[] = [];
  let total = 0;
  for (const { name, accepted } of slots) {
    const pick = available.find((p) => !used.has(p) && eligibleFor(p, accepted));
    if (pick) {
      used.add(pick);
      total += pick.points;
    }
    lineup.push({ slot: name, player: pick ?? null });
  }
  return { points: round2(total), lineup };
}

/** Actual points: authoritative value if present, else sum of started players. */
export function actualPoints(tw: TeamWeek): number {
  if (tw.actualPoints !== null) return tw.actualPoints;
  const started = tw.players.filter(
    (p) => !BENCH_POSITIONS.has(p.selectedPosition),
  );
  return round2(started.reduce((sum, p) => sum + p.points, 0));
}

export const startedPlayers = (tw: TeamWeek): Player[] =>
  tw.players.filter((p) => !BENCH_POSITIONS.has(p.selectedPosition));

export const benchPlayers = (tw: TeamWeek): Player[] =>
  tw.players.filter((p) => BENCH_POSITIONS.has(p.selectedPosition));

export function optimizeTeamWeek(
  tw: TeamWeek,
  league: Pick<LeagueData, "rosterSlots" | "slotEligibility">,
): OptimalResult {
  return optimizeLineup(tw.players, league.rosterSlots, league.slotEligibility);
}
